import clsx from 'clsx';
import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { IconDotsVertical } from '@tabler/icons-react';

import { useFormatText } from '@/hooks/useFormatText';
import { useSettingsStore } from '@/store/useSettingsStore';

interface IAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  isDanger?: boolean;
  disabled?: boolean;
  className?: string;
}

interface IActionDropdownProps {
  actions: IAction[];
  className?: string;
}

const ActionDropdown: React.FC<IActionDropdownProps> = ({
  actions,
  className,
}) => {
  const { formatText } = useFormatText();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const direction = useSettingsStore((s) => s.settings.direction);
  const isRTL = direction === 'rtl';

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isOpen) {
      // Close all other dropdowns first
      document.dispatchEvent(new CustomEvent('close-all-dropdowns'));

      // Use setTimeout to ensure other dropdowns close first
      setTimeout(() => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const scrollX = window.scrollX || window.pageXOffset;
          const scrollY = window.scrollY || window.pageYOffset;

          setPosition({
            top: rect.bottom + scrollY + 5,
            left: isRTL ? rect.right + scrollX - 40 : rect.left + scrollX - 80,
          });
        }
        setIsOpen(true);
      }, 0);
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleCloseAll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      // Use setTimeout to add listeners after the current event loop
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      document.addEventListener('close-all-dropdowns', handleCloseAll);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('close-all-dropdowns', handleCloseAll);
    };
  }, [isOpen]);

  return (
    <>
      <div
        ref={buttonRef}
        onClick={toggle}
        data-testid="action-dropdown"
        className="cursor-pointer p-1 rounded-full hover:bg-neutral-100 w-fit mx-auto"
      >
        <IconDotsVertical size={20} color="#717784" />
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={clsx(
              'fixed z-9999 flex flex-col bg-white border border-neutral-light-200 rounded-lg shadow-lg py-1 w-32',
              className
            )}
            style={{
              top: position.top,
              left: position.left,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {actions.map((action) => (
              <button
                key={action.key}
                disabled={action.disabled}
                className={clsx(
                  'px-4 py-1.5 text-base text-start hover:bg-neutral-50 w-full flex items-center gap-2 cursor-pointer',
                  action.isDanger
                    ? 'text-error-secondary'
                    : 'text-neutral-primary',
                  action.disabled &&
                    'opacity-50 cursor-not-allowed! hover:bg-white',
                  action.className
                )}
                onClick={() => {
                  if (!action.disabled) {
                    action.onClick();
                    setIsOpen(false);
                  }
                }}
              >
                {action.icon}
                {formatText(action.label)}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default ActionDropdown;
